// =============================================================================
// UserPopover — small floating profile card anchored to the clicked element.
// =============================================================================
// Triggered by clicking a user's avatar or name anywhere in the app. Shows
// display name, username, role, online status, "joined" date, and a quick
// "Send Message" button that opens (or creates) a DM with that user.
//
// Positioning is auto-flipped to keep the card on-screen. Clicking outside
// or pressing Esc dismisses.
//
// Usage (two patterns):
//
//   1. Wrap a clickable element with <UserClickTarget userId={..} member={..}>
//      The wrapped content becomes a button that opens the popover for that
//      user, anchored to itself.
//
//   2. Use the lower-level useUserPopover() hook to manually control state
//      from anywhere (we don't currently need this; export is for futureproofing).
// =============================================================================

import { createContext, useContext, useEffect, useRef, useState, useCallback, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Avatar, Button, Icon, Spinner } from './ui';
import { useApp } from '../store';
import { api, ApiError } from '../api';
import { startCall } from '../calls';
import { useVoice } from './voiceContext';
import { roleLabel, roleBadgeClass, hasPermission, outranks } from '../permissions';
import { useIsServerMuted } from '../voiceMutes';
import { useVoicePresence } from '../hooks';
import { toast } from './Toast';

/** The server the viewer is looking at + their role in it, for moderator
 *  actions. Provided by AppShell; null outside a server (DMs, profiles). */
export const ViewerServerContext = createContext<{
  serverId: string;
  role: string | null;
  voiceChannelIds: ReadonlySet<string>;
} | null>(null);
import type { Member, FriendStatusWith, MutualServerBrief } from '../types';

// =============================================================================
// Anchor wrapper — turns its children into a clickable trigger that opens
// the popover. Uses a button so keyboard nav works.
// =============================================================================

export function UserClickTarget({
  userId,
  username,
  displayName,
  avatarUrl,
  serverRole,
  joinedAt,
  className = '',
  children,
  // Some callers want to suppress the popover entirely (e.g. clicking yourself
  // — there's nothing useful to show, and "Send Message" to self isn't a thing).
  disabled = false,
}: {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  serverRole?: 'owner' | 'admin' | 'moderator' | 'member' | null;
  joinedAt?: string | null;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  const me = useApp((s) => s.user);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Disable for our own user — clicking yourself shouldn't open anything.
  const effectiveDisabled = disabled || me?.id === userId;

  if (effectiveDisabled) {
    // Render the children inline without making them a clickable trigger.
    // span lets it inherit layout from the parent without turning into a row.
    return <span className={className}>{children}</span>;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onContextMenu={(e) => {
          // Right-clicking a user opens the same card (View Profile / Send
          // Message / Add Friend / Block) — no separate menu to maintain.
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={`text-left ${className}`}
      >
        {children}
      </button>
      {open && (
        <UserPopover
          userId={userId}
          username={username}
          displayName={displayName}
          avatarUrl={avatarUrl}
          serverRole={serverRole ?? null}
          joinedAt={joinedAt ?? null}
          anchor={triggerRef.current}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// =============================================================================
// The popover itself
// =============================================================================

function UserPopover({
  userId,
  username,
  displayName,
  avatarUrl,
  serverRole,
  joinedAt,
  anchor,
  onClose,
}: {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  serverRole: 'owner' | 'admin' | 'moderator' | 'member' | null;
  joinedAt: string | null;
  anchor: HTMLElement | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const onlineUserIds = useApp((s) => s.onlineUserIds);
  const isOnline = onlineUserIds.has(userId);
  const { status, mutual, busy, run } = useRelationship(userId);

  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [openingDm, setOpeningDm] = useState(false);
  const [placingCall, setPlacingCall] = useState(false);
  const [dmError, setDmError] = useState<string | null>(null);

  // Position the popover relative to the anchor, flipping/shifting to stay on
  // screen. Recomputed not just on mount but whenever the popover's own size
  // changes — its content grows asynchronously (the relationship-actions
  // section fetches friend status, then renders Add Friend / Block buttons), so
  // a one-shot measurement would leave the taller card bleeding off the bottom.
  const reposition = useCallback(() => {
    if (!anchor || !popoverRef.current) return;
    const anchorBox = anchor.getBoundingClientRect();
    const popBox = popoverRef.current.getBoundingClientRect();
    const margin = 8;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Default: anchor it to the right of the trigger, top-aligned with it.
    let left = anchorBox.right + margin;
    let top = anchorBox.top;

    // If overflowing right edge, place to the left of the trigger.
    if (left + popBox.width + margin > viewportW) {
      left = anchorBox.left - popBox.width - margin;
    }
    // If still off-screen left (very narrow viewport), pin to viewport with margin.
    if (left < margin) left = margin;

    // If overflowing bottom edge, shift up enough to fit.
    if (top + popBox.height + margin > viewportH) {
      top = Math.max(margin, viewportH - popBox.height - margin);
    }
    if (top < margin) top = margin;

    setPos({ left, top });
  }, [anchor]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  // Re-run positioning when the card resizes (async content) or the window does.
  useEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => reposition());
    ro.observe(el);
    window.addEventListener('resize', reposition);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, [reposition]);

  // Close on outside click / Esc. Use mousedown so it fires before any inner
  // clicks (e.g. a button inside us would get cancelled if we used click).
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target as Node)) return;
      // Also ignore clicks on the original anchor — those toggle via the trigger.
      if (anchor && anchor.contains(e.target as Node)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  async function sendMessage() {
    setOpeningDm(true);
    setDmError(null);
    try {
      const thread = await api.openDm({ userId });
      onClose();
      navigate(`/dm/${thread.channelId}`);
    } catch (err) {
      setDmError(err instanceof ApiError ? err.message : 'Failed to open DM');
      setOpeningDm(false);
    }
  }

  async function placeCall() {
    setPlacingCall(true);
    setDmError(null);
    try {
      // Resolve (or create) the DM channel, jump to it, then ring. Runs from a
      // click so the AudioContext can start for the outgoing ringback.
      const thread = await api.openDm({ userId });
      onClose();
      navigate(`/dm/${thread.channelId}`);
      startCall(thread.channelId, displayName || username);
    } catch (err) {
      setDmError(err instanceof ApiError ? err.message : 'Failed to start call');
      setPlacingCall(false);
    }
  }

  const name = displayName || username;
  const joinedFormatted = joinedAt ? formatJoined(joinedAt) : null;

  // Render into document.body so the card escapes any dimmed/clipped/transformed
  // ancestor. In particular, offline member rows carry opacity-50; rendered
  // inline the card would inherit that and look washed out (CSS opacity applies
  // to all descendants, even fixed-positioned ones). Portaling keeps it fully
  // solid and readable regardless of where the trigger lives.
  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`Profile: ${name}`}
      className="fixed z-60 hybrid-card w-64 select-text overflow-y-auto"
      // Hide until positioned to avoid the flicker at (0,0) on first paint.
      style={{
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
        // Never exceed the viewport — on a very short window the card scrolls
        // internally rather than running off the bottom edge.
        maxHeight: 'calc(100vh - 16px)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="hybrid-card-header py-1.5!">
        <Icon name="user" size={11} />
        <span className="truncate flex-1">Profile</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-[12px] leading-none w-5 h-5 hover:bg-white/10 rounded-sm flex items-center justify-center"
        >
          ×
        </button>
      </div>
      <div className="bg-surface p-3 space-y-3">
        <div className="flex items-start gap-3">
          <div className="relative">
            <Avatar name={name} url={avatarUrl} size={56} />
            <span
              className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-surface ${
                isOnline ? 'bg-online' : 'bg-text-subtle'
              }`}
              title={isOnline ? 'Online' : 'Offline'}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-text truncate">{name}</div>
            <div className="text-xs text-text-muted truncate">@{username}</div>
            {serverRole && serverRole !== 'member' && (
              <div className="mt-1.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${roleBadgeClass(serverRole)}`}>
                  {roleLabel(serverRole)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Info group — member-since + mutual servers read as context about
            who this is, so they sit with the identity (no dividers between). */}
        {(joinedFormatted || mutual.length > 0) && (
          <div className="space-y-1.5">
            <div className="text-[11px] text-text-subtle flex flex-wrap items-center gap-x-1.5">
              {joinedFormatted && (
                <span>
                  Member since <span className="text-text-muted">{joinedFormatted}</span>
                </span>
              )}
              {joinedFormatted && mutual.length > 0 && <span aria-hidden>·</span>}
              {mutual.length > 0 && (
                <span>
                  {mutual.length} mutual server{mutual.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <MutualServers servers={mutual} />
          </div>
        )}

        <VoiceVolumeSection userId={userId} />
        <VoiceModerationSection userId={userId} name={name} targetRole={serverRole} />

        {dmError && (
          <div className="text-xs text-danger bg-danger/10 px-2 py-1 rounded-sm">{dmError}</div>
        )}

        {/* Primary actions */}
        <div className="flex gap-2 border-t border-border-soft pt-3">
          <Button
            variant="primary"
            onClick={placeCall}
            disabled={placingCall || openingDm}
            className="flex-1 justify-center"
            title={`Call ${name}`}
          >
            {placingCall ? <Spinner size={12} /> : <Icon name="phone" size={12} />}
            {placingCall ? 'Calling…' : 'Call'}
          </Button>
          <Button
            onClick={sendMessage}
            disabled={openingDm || placingCall}
            className="flex-1 justify-center"
          >
            {openingDm ? <Spinner size={12} /> : <Icon name="send" size={12} />}
            {openingDm ? 'Opening…' : 'Message'}
          </Button>
        </div>

        {/* Secondary actions — one compact row instead of three stacked,
            bordered button rows: View Profile · friend action · Block. */}
        <SecondaryActions
          userId={userId}
          username={username}
          status={status}
          busy={busy}
          run={run}
          onClose={onClose}
        />
      </div>
    </div>,
    document.body
  );
}

// =============================================================================
// VoiceModerationSection — server mute / disconnect, for viewers who hold
// moderate_voice AND outrank the target (the backend enforces the same gate;
// this only decides whether to show the buttons). Server mute is server-wide
// and works pre-emptively; Disconnect only shows while the target is in one of
// this server's voice channels.
// =============================================================================
function VoiceModerationSection({
  userId,
  name,
  targetRole,
}: {
  userId: string;
  name: string;
  targetRole: string | null;
}) {
  const viewer = useContext(ViewerServerContext);
  const me = useApp((s) => s.user);
  const serverMuted = useIsServerMuted(viewer?.serverId, userId);
  const voicePresence = useVoicePresence();
  const [busy, setBusy] = useState(false);

  if (!viewer || !me || me.id === userId) return null;
  if (!hasPermission(viewer.role, 'moderate_voice') || !outranks(viewer.role, targetRole)) return null;

  const voiceChannelId = voicePresence.get(userId);
  const inThisServersVoice = !!voiceChannelId && viewer.voiceChannelIds.has(voiceChannelId);

  async function act(fn: () => Promise<unknown>, done: string, failed: string) {
    setBusy(true);
    try {
      await fn();
      toast.success(done);
    } catch (err) {
      toast.error(err, failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-border-soft pt-3">
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-1.5">
        Voice moderation
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() =>
            act(
              () => api.setVoiceServerMute(viewer.serverId, userId, !serverMuted),
              serverMuted ? `Lifted ${name}'s server mute` : `Server-muted ${name}`,
              serverMuted ? "Couldn't lift the server mute" : "Couldn't server-mute",
            )
          }
          disabled={busy}
          aria-pressed={serverMuted}
          className="flex-1 justify-center"
          title={serverMuted ? `Let ${name} speak in this server again` : `Stop ${name} speaking in any voice channel in this server`}
        >
          <Icon name={serverMuted ? 'mic' : 'mic-off'} size={12} />
          {serverMuted ? 'Unmute' : 'Server mute'}
        </Button>
        {inThisServersVoice && (
          <Button
            variant="danger"
            onClick={() =>
              act(
                () => api.disconnectFromVoice(voiceChannelId!, userId),
                `Disconnected ${name} from voice`,
                "Couldn't disconnect",
              )
            }
            disabled={busy}
            className="flex-1 justify-center"
            title={`Remove ${name} from the voice channel (they can rejoin)`}
          >
            <Icon name="phone-off" size={12} />
            Disconnect
          </Button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// useRelationship — fetch friend status + mutual servers for a user, with a
// `run()` helper that performs a mutation then refetches. Shared by the
// popover's info group (mutual servers) and its secondary action row.
// =============================================================================
function useRelationship(userId: string) {
  const [status, setStatus] = useState<FriendStatusWith | null>(null);
  const [mutual, setMutual] = useState<MutualServerBrief[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.friendStatus(userId);
      setStatus(r.status);
      setMutual(r.mutualServers);
    } catch {
      setStatus(null);
      setMutual([]);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      try {
        await fn();
        await load();
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  return { status, mutual, busy, run };
}

// =============================================================================
// MutualServers — compact "how you know them" chip row (capped at 3 + overflow)
// =============================================================================
function MutualServers({ servers }: { servers: MutualServerBrief[] }) {
  if (servers.length === 0) return null;
  const shown = servers.slice(0, 3);
  const extra = servers.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((s) => (
        <span
          key={s.id}
          title={s.name}
          className="flex items-center gap-1.5 max-w-[120px] bg-elevated/60 rounded-aim pl-1 pr-2 py-0.5"
        >
          <Avatar name={s.name} url={s.iconUrl} size={16} />
          <span className="text-[11px] text-text-muted truncate">{s.name}</span>
        </span>
      ))}
      {extra > 0 && <span className="text-[11px] text-text-subtle">+{extra} more</span>}
    </div>
  );
}

// =============================================================================
// SecondaryActions — the single footer row: View Profile · friend action ·
// Block. Friend/block controls only appear once the relationship is known.
// =============================================================================
function SecondaryActions({
  userId,
  username,
  status,
  busy,
  run,
  onClose,
}: {
  userId: string;
  username: string;
  status: FriendStatusWith | null;
  busy: boolean;
  run: (fn: () => Promise<unknown>) => Promise<void>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const linkBtn =
    'text-xs px-2 py-1 rounded-sm hover:bg-elevated disabled:opacity-50 transition-colors inline-flex items-center gap-1';

  // Block is offered for anyone except ourselves or someone who's blocked us.
  const canBlock = status !== null && status !== 'self' && status !== 'blocked_by';

  return (
    <div className="flex items-center gap-1 flex-wrap border-t border-border-soft pt-2">
      <button
        type="button"
        onClick={() => {
          onClose();
          navigate(`/u/${encodeURIComponent(username)}`);
        }}
        className={`${linkBtn} text-text-muted`}
      >
        <Icon name="user" size={11} /> Profile
      </button>

      {status === 'none' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => api.addFriend({ userId }))}
          className={`${linkBtn} text-[#3BA55D] font-semibold`}
        >
          <Icon name="plus" size={11} /> Add Friend
        </button>
      )}
      {status === 'pending_in' && (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => api.acceptFriend(userId))}
            className={`${linkBtn} text-[#3BA55D] font-semibold`}
          >
            <Icon name="check" size={11} /> Accept
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => api.removeFriendRequest(userId))}
            className={`${linkBtn} text-text-muted`}
          >
            Decline
          </button>
        </>
      )}
      {status === 'pending_out' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => api.removeFriendRequest(userId))}
          className={`${linkBtn} text-text-muted`}
        >
          Cancel request
        </button>
      )}
      {status === 'friends' && (
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => api.unfriend(userId))}
          className={`${linkBtn} text-text-muted`}
        >
          Remove Friend
        </button>
      )}

      {(status === 'blocked' || canBlock) && (
        <div className="ml-auto">
          {status === 'blocked' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => api.unblockUser(userId))}
              className={`${linkBtn} text-text-muted`}
            >
              Unblock
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => api.blockUser(userId))}
              className={`${linkBtn} text-danger`}
            >
              Block
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Format an ISO joined-at into a readable relative date.
//   < 1 hour: "just now"
//   < 1 day:  "a few hours ago"
//   < 30 d:   "X days ago"
//   < 1 yr:   "March 14"
//   else:     "March 14, 2025"
// =============================================================================
function formatJoined(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffHours / 24;

  if (diffHours < 1) return 'just now';
  if (diffHours < 24) return 'today';
  if (diffDays < 2) return 'yesterday';
  if (diffDays < 30) return `${Math.floor(diffDays)} days ago`;

  const sameYear = then.getFullYear() === now.getFullYear();
  return then.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

// Re-export Member for callers who want to derive props from it.
export type { Member };

// =============================================================================
// VoiceVolumeSection — volume slider for a remote participant
// =============================================================================
// Only renders when:
//   1. We're connected to a voice room AND
//   2. The user this popover is for is a participant in that room
// Otherwise we have nothing to act on (no track to adjust gain on).
//
// 0% = muted (just for me; the speaker doesn't know), 100% = normal,
// 200% = +6dB. Live update on input — feedback is instant. The store
// persists across sessions, so the next time this user joins voice your
// preference is automatically re-applied.
// =============================================================================
function VoiceVolumeSection({ userId }: { userId: string }) {
  const voice = useVoice();
  const me = useApp((s) => s.user);

  // Only show for OTHER users in OUR voice room.
  if (!voice.inRoom) return null;
  if (me?.id === userId) return null;
  const inRoom = voice.participants.some((p) => p.identity === userId);
  if (!inRoom) return null;

  const volume = voice.getVolume(userId);
  // Slider operates in percent (0..200) for nicer UI granularity.
  const pct = Math.round(volume * 100);

  return (
    <div className="border-t border-border-soft pt-2 space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-text-subtle font-bold">
          Volume in voice
        </span>
        <span className="text-xs text-text-muted font-mono tabular-nums">{pct}%</span>
      </div>
      <div className="flex items-center gap-2">
        <Icon name="speaker" size={12} className="text-text-subtle" />
        <input
          type="range"
          min={0}
          max={200}
          step={5}
          value={pct}
          onChange={(e) => voice.setVolume(userId, Number(e.target.value) / 100)}
          className="flex-1 accent-accent"
          aria-label="Volume"
        />
        {/* Always rendered (visibility toggled, not mounted) so the slider
            width is constant as the value crosses 100% — unmounting it
            mid-drag reflows the flex-1 track and makes the value oscillate. */}
        <button
          type="button"
          onClick={() => voice.setVolume(userId, null)}
          className="text-[10px] text-text-subtle hover:text-text px-1"
          title="Reset to default"
          style={{ visibility: pct === 100 ? 'hidden' : 'visible' }}
          disabled={pct === 100}
          tabIndex={pct === 100 ? -1 : 0}
          aria-hidden={pct === 100}
        >
          reset
        </button>
      </div>
    </div>
  );
}
